import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { listMySubscriptions, type MySubscriptionItem } from '../widget-api/my-subscriptions-list';
import { PATH } from '../widget-lib/path-routes';

type BalanceDisplay = {
  label: string;
  used: number;
  total: number | null;
  valid_until: string | null;
  status: string;
};

function getBalances(subs: MySubscriptionItem[]): BalanceDisplay[] {
  return subs
    .filter((s) => s.status === 'active' || s.status === 'expiring_soon')
    .map((s) => ({
      label: s.plan_name || s.plan_type,
      used: s.visits_used || Number(s.balance_used) || 0,
      total: s.visits_total || (s.balance_total ? Number(s.balance_total) : null),
      valid_until: s.valid_until,
      status: s.status,
    }));
}

export default function PayLoyaltyPage() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const { accessToken, bootstrapHint, orgSlug, me } = useEmbedBootstrap();
  const [subscriptions, setSubscriptions] = useState<MySubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const items = await listMySubscriptions(accessToken);
      setSubscriptions(items);
    } catch {
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const balances = getBalances(subscriptions);

  if (bootstrapHint && !orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Оплата лояльностью</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Оплата лояльностью</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы использовать абонемент или сертификат.</p>
        <div className="flex flex-wrap gap-2">
          <Link to={`${PATH.signIn}${suffix}`}
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
            Войти
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Оплата лояльностью</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          {me ? (me.full_name || 'Пользователь') : ''}
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Загрузка...</p>
      ) : balances.length > 0 ? (
        <>
          <section className="space-y-3">
            <h2 className="font-medium text-sm">Действующие абонементы и сертификаты</h2>
            <ul className="space-y-2">
              {balances.map((b, i) => (
                <li key={i} className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-sm">
                  <p className="font-medium text-[var(--text)]">{b.label}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    Использовано: {b.used}{b.total != null ? ` из ${b.total}` : ''}
                  </p>
                  {b.valid_until && (
                    <p className="text-xs text-[var(--text-muted)]">
                      Действует до: {new Date(b.valid_until).toLocaleDateString('ru-RU')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
          <p className="text-sm text-[var(--text-muted)]">
            Примените абонемент при записи на занятие — выберите способ оплаты «Абонемент / сертификат» в форме.
          </p>
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[var(--text-muted)]">Нет действующих абонементов или сертификатов.</p>
          <div className="flex flex-wrap gap-2">
            <Link to={`${PATH.subscriptionPlans}${suffix}`}
              className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
              Купить абонемент
            </Link>
            <Link to={`${PATH.schedule}${suffix}`}
              className="inline-flex h-9 items-center justify-center rounded-sm border border-[var(--btn-secondary-border)] bg-[var(--btn-secondary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-secondary-fg)] hover:bg-[var(--btn-secondary-bg-hover)]">
              Записаться на занятие
            </Link>
          </div>
        </div>
      )}

      <details className="group text-sm border-t border-[var(--border)] pt-4">
        <summary className="cursor-pointer text-[var(--text-muted)] hover:text-[var(--text)]">Все подписки</summary>
        <div className="mt-3 space-y-2 pl-2">
          {subscriptions.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)]">Нет подписок.</p>
          ) : (
            <ul className="space-y-2">
              {subscriptions.map((s) => (
                <li key={s.id} className="rounded-lg border border-[var(--border)] p-3 text-sm">
                  <p className="font-medium text-[var(--text)]">{s.plan_name || s.plan_type}</p>
                  <p className="text-xs text-[var(--text-muted)]">{s.status}</p>
                  {s.branch_name && <p className="text-xs text-[var(--text-muted)]">{s.branch_name}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>

      <p className="text-sm text-[var(--text-muted)]">
        Оплата банковской картой через шлюз —{' '}
        <Link className="text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.payCheckout}${suffix}`}>
          отдельный сценарий
        </Link>
        .
      </p>
    </div>
  );
}
